import { MantineProvider } from "@mantine/core";
import "@mantine/core/styles.css";
import { superAgentsTheme } from "@/design-system/theme/superAgentsTheme";
import { superAgentsCssVariablesResolver } from "@/design-system/theme/cssVariablesResolver";
import { ComponentType, useEffect, useState } from "react";

type NotificationsComponent = ComponentType;

export default function ThemeProvider({
  children,
}: {
  children?: React.ReactNode;
}) {
  const [NotificationsComponent, setNotificationsComponent] =
    useState<NotificationsComponent | null>(null);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const idleWindow = window as Window &
      Partial<{
        requestIdleCallback: (
          callback: IdleRequestCallback,
          options?: IdleRequestOptions
        ) => number;
        cancelIdleCallback: (id: number) => void;
      }>;

    let timeoutId: number | undefined;

    const loadNotifications = () => {
      void import("./DeferredNotifications").then((module) => {
        setNotificationsComponent(() => module.default);
      });
    };

    const scheduleLoad = () => {
      if (idleWindow.requestIdleCallback) {
        const callbackId = idleWindow.requestIdleCallback(loadNotifications, {
          timeout: 3000,
        });

        return () => idleWindow.cancelIdleCallback?.(callbackId);
      }

      timeoutId = window.setTimeout(loadNotifications, 1500);

      return () => {
        if (timeoutId !== undefined) {
          window.clearTimeout(timeoutId);
        }
      };
    };

    if (document.readyState === "complete") {
      return scheduleLoad();
    }

    const handleLoad = () => {
      cleanup = scheduleLoad();
    };

    let cleanup: (() => void) | undefined;
    window.addEventListener("load", handleLoad, { once: true });

    return () => {
      window.removeEventListener("load", handleLoad);
      cleanup?.();
      if (timeoutId !== undefined) {
        window.clearTimeout(timeoutId);
      }
    };
  }, []);

  return (
    <MantineProvider
      theme={superAgentsTheme}
      cssVariablesResolver={superAgentsCssVariablesResolver}
      defaultColorScheme="light">
      {NotificationsComponent ? <NotificationsComponent /> : null}
      {children}
    </MantineProvider>
  );
}
